import { FileManager } from 'components/FileManager'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useCasoService } from 'hooks/useCasoService'
import { useSetorService } from 'hooks/useSetorService'
import { useAuth } from 'hooks/useAuth'
import useStyles from './styles'

import { cutMaxLength } from 'utils/helpers'
import useRouter from 'utils/useRouter'
import { Header, Page, SelecionarData, CustomButton, SecaoGenerica } from 'components'
import AutocompleteNative from '@material-ui/lab/Autocomplete'
import { CircularProgress, Dialog, DialogActions, DialogTitle, Divider, Grid, TextField, Typography } from '@material-ui/core'
import { setIdCaso } from 'actions'
import { useParams } from 'react-router'
import { useRequisicaoService } from 'hooks/useRequisicaoService'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SaveIcon from '@material-ui/icons/Save'
import DeleteIcon from '@material-ui/icons/Delete'

const maxLength4000 = (newValue) => cutMaxLength(newValue, 4000)
const maxLength250 = (newValue) => cutMaxLength(newValue, 250)

const TIPOS_PDF = ['pdf']

const MainForm = (props) => {
  const { route, caso, processandoCaso, arquivos, children } = props
  const classes = useStyles()
  const { currentUser } = useAuth()
  const router = useRouter()
  const params = useParams()
  const dispatch = useDispatch()
  const setorService = useSetorService()
  const casoService = useCasoService()
  const requisicaoService = useRequisicaoService()
  const [setores, setSetores] = useState([])
  const [isFormError, setIsFormError] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const [openAlertDelete, setOpenAlertDelete] = useState(false)
  const [requisicaoImportar, setRequisicaoImportar] = useState(null)
  const [processandoImportar, setProcessandoImportar] = useState(false)
  const [processandoRequisicao, setProcessandoRequisicao] = useState(false)

  // Local form states
  const [setorResponsavel, setSetorResponsavel] = useState(null)
  const [processoSei, setProcessoSei] = useState('')
  const [descricao, setDescricao] = useState('')
  const [data, setData] = useState(new Date())

  const fetchSetores = useCallback(async () => {
    const response = await setorService.listarSetoresAsync()
    setSetores(response)
  }, [])

  const handleClickOpenAlertDelete = useCallback(() => {
    setOpenAlertDelete(true)
  }, [])

  const handleCloseAlertDelete = useCallback(() => {
    setOpenAlertDelete(false)
  }, [])

  const handleDelete = useCallback(
    async (event) => {
      event.preventDefault()
      await casoService.deletarCaso(caso.id, setProcessandoOperacao)
      router.history.push('/casos')
    },
    [caso, router.history]
  )

  const setDataCaso = useCallback((novaData) => {
    if (novaData !== 'Invalid date') {
      setData(novaData)
    }
  }, [])

  const confirmarOperacao = useCallback(async () => {
    if (!isFormError) {
      setProcessandoOperacao(true)
      let casoResultado = null
      const formValues = {
        setorResponsavel,
        processoSei,
        descricao,
        data,
        arqs: arquivos || [],
      }
      if (route.editar) {
        casoResultado = await casoService.editarCaso(caso.id, formValues)
      } else {
        casoResultado = await casoService.cadastrarCaso(formValues)
        if (requisicaoImportar != null) {
          await requisicaoService.importarRequisicaoNoCaso(casoResultado.id, requisicaoImportar, setProcessandoImportar)
        }
      }
      dispatch(setIdCaso(casoResultado.id))
      setProcessandoOperacao(false)
      router.history.push(`/casos/caso/detalhar/${casoResultado.id}`)
    }
  }, [setorResponsavel, processoSei, descricao, data, arquivos, route, isFormError, caso, requisicaoImportar, dispatch, router.history])


  const fetchRequisicaoImportar = useCallback(async () => {
    const response = await requisicaoService.listarRequisicaoPorIdAsync(params.idRequisicao, setProcessandoRequisicao)
    setRequisicaoImportar(response)
    if (response != null) {
      setSetorResponsavel(response.setorSolicitante || null)
      setDescricao(response.resumo_ocorrencia || '')
    }
  }, [params.idRequisicao])


  useEffect(() => {
    if (route.casoRelacionado) {
      fetchRequisicaoImportar()
    }
  }, [route.casoRelacionado, fetchRequisicaoImportar])

  useEffect(() => {
    let formError = !setorResponsavel
    const dateObj = new Date(data)

    if (isNaN(dateObj.getTime())) {
      formError = true
    }
    setIsFormError(formError)
  }, [setorResponsavel, data])

  useEffect(() => {
    fetchSetores()
  }, [fetchSetores])

  useEffect(() => {
    if (route.editar && caso?.id) {
      setSetorResponsavel(caso.setRes || null)
      setProcessoSei(caso.processoSei || '')
      setDescricao(caso.descricao || '')
      setData(caso.data ? new Date(caso.data) : new Date())
    }
  }, [caso, route.editar])

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} pathname={router.location.pathname} router={router} />

      {processandoCaso ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <form id="form-caso" onSubmit={(e) => e.preventDefault()}>

          <Grid container spacing={3} className={classes.fieldGroup}>
            <Grid item xs={12}>
              <AutocompleteNative
                value={setorResponsavel}
                name="setorResponsavel"
                noOptionsText="Sem informações"
                options={setores}
                onChange={(event, newValue) => {
                  setSetorResponsavel(newValue)
                }}
                getOptionLabel={(option) => `${option.nome.toUpperCase()} - ${option.sigla} / ${option.divisao.regional.nome.toUpperCase()} / ${option.divisao.regional.orgao.sigla.toUpperCase()}`}
                renderOption={(option) => `${option.nome.toUpperCase()} - ${option.sigla} / ${option.divisao.regional.nome.toUpperCase()} / ${option.divisao.regional.orgao.sigla.toUpperCase()}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="SETOR RESPONSÁVEL"
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="NÚMERO DO PROCESSO SEI"
                name="processoSei"
                onChange={(e) => setProcessoSei(maxLength250(e.target.value))}
                value={processoSei}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Typography variant="caption" color="textSecondary" style={{ marginTop: '4px', display: 'block' }}>
                Caracteres restantes: {250 - processoSei.length}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                minRows={4}
                multiline
                label="DESCRIÇÃO DO CASO"
                name="descricao"
                variant="outlined"
                onChange={(e) => setDescricao(maxLength4000(e.target.value))}
                value={descricao}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Typography variant="caption" color="textSecondary" style={{ marginTop: '4px', display: 'block' }}>
                Caracteres restantes: {4000 - descricao.length}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SelecionarData
                xs={12}
                sm={12}
                md={12}
                dataBD={data}
                label="DATA DO CASO"
                callback={setDataCaso}
                required
              />
            </Grid>
          </Grid>


          <SecaoGenerica titulo="Arquivos Relacionados">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {children}
              </Grid>
            </Grid>
          </SecaoGenerica>

          <Divider style={{ margin: '32px 0 16px 0' }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
                <CustomButton variant="outlined" colorType="primary" startIcon={<ArrowBackIcon />} onClick={() => router.history.goBack()}>
                  Cancelar
                </CustomButton>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {currentUser.cargo?.id === 1 && route.editar && (
                    <CustomButton variant="contained" colorType="error" startIcon={<DeleteIcon />} onClick={handleClickOpenAlertDelete}>
                      Excluir Caso
                    </CustomButton>
                  )}
                  <CustomButton variant="contained" colorType="secondary" startIcon={<SaveIcon />} disabled={isFormError} isLoading={processandoOperacao} onClick={confirmarOperacao}>
                    {route.editar ? 'Atualizar Caso' : 'Cadastrar Caso'}
                  </CustomButton>
                </div>
              </div>
            </Grid>
          </Grid>
        </form>
      )}

      <Dialog open={openAlertDelete} onClose={handleCloseAlertDelete} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Tem certeza que deseja deletar?</DialogTitle>
        <DialogActions>
          <CustomButton onClick={handleCloseAlertDelete} colorType="primary" variant="outlined">
            Cancelar
          </CustomButton>
          <CustomButton onClick={handleDelete} colorType="error" variant="contained">
            Confirmar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Page>
  )
}

export const EditarCaso = (props) => {
  const params = useParams()
  const { route } = props
  const [caso, setCaso] = useState(null)
  const [processandoCaso, setProcessandoCaso] = useState(false)
  const casoService = useCasoService()
  const [arquivos, setArquivos] = useState([])
  const arquivosIniciais = useMemo(() => caso?.arqs.map((arquivo) => arquivo.id) || [], [caso])

  const fetchCaso = useCallback(async () => {
    const response = await casoService.listarCasoDetalhadoAsync(params.id, setProcessandoCaso)
    setCaso(response)
  }, [params.id])

  useEffect(() => {
    if (route.editar) {
      fetchCaso()
    }
  }, [fetchCaso, route.editar])

  return (
    <MainForm processandoCaso={processandoCaso} arquivos={arquivos} route={route} caso={caso}>
      <FileManager rotaUpload="/arquivo/caso" rotaLoadImagem="/arquivo/caso" setArquivosPai={setArquivos} arquivosIniciais={arquivosIniciais} tipos={TIPOS_PDF} />
    </MainForm>
  )
}
